<template>
  <div class="fixed bottom-6 right-6 z-[9999] flex flex-col items-end font-sans">

    <Transition name="slide-up">
      <div v-if="isOpen" class="w-[90vw] max-w-[380px] h-[510px] bg-neural-card/95 backdrop-blur-2xl border border-neural-border rounded-[2rem] shadow-2xl flex flex-col overflow-hidden mb-4">

        <!-- HEADER DEL CHAT (SAAS / DEEP TECH) -->
        <div class="p-4 border-b border-neural-border bg-[#131316] flex items-center justify-between shadow-md z-10">
          <div class="flex items-center gap-3">
            <div class="relative">
              <!-- Avatar del Agente Cognitivo (Estilo abstracto/Tech) -->
              <img
                src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=150&auto=format&fit=crop"
                alt="Agente Neurals"
                class="w-10 h-10 rounded-full object-cover border-2 border-neural-primary shadow-glow-primary"
              >
              <div class="absolute bottom-0 right-0 w-3 h-3 bg-neural-online border-2 border-[#131316] rounded-full"></div>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-bold text-neural-text leading-none truncate">{{ chat.agent_name || 'Agente Neurals' }}</h3>
              <p class="text-[10px] uppercase tracking-wider mt-1 transition-all duration-300 font-semibold"
                 :class="isTyping ? 'text-neural-primary translate-x-1' : 'text-neural-muted'">
                {{ isTyping ? chat.status_typing || 'Procesando...' : chat.role || 'Soporte Cognitivo' }}
              </p>
            </div>
          </div>

          <button @click="toggleChat" class="text-neural-muted hover:text-neural-text transition-colors p-1 cursor-pointer">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        <!-- ÁREA DE MENSAJES -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth bg-neural-bg">

          <div v-for="(msg, index) in messages" :key="index"
               :class="['flex w-full', msg.isUser ? 'justify-end' : 'justify-start']">

            <div :class="[
              'max-w-[85%] p-3.5 text-sm leading-relaxed transition-all duration-300',
              msg.isUser
                ? 'bg-neural-primary text-white font-medium rounded-2xl rounded-tr-none shadow-glow-primary'
                : 'bg-neural-card text-neural-text border border-neural-border rounded-2xl rounded-tl-none font-normal shadow-sm'
            ]">
              {{ msg.text }}
            </div>
          </div>

          <!-- INDICADOR DE ESCRIBIENDO / CALCULANDO -->
          <div v-if="isTyping" class="flex justify-start w-full">
            <div class="bg-neural-card border border-neural-border rounded-2xl rounded-tl-none p-4 flex gap-1.5 items-center w-fit shadow-sm">
              <span class="w-1.5 h-1.5 bg-neural-primary rounded-full animate-bounce"></span>
              <span class="w-1.5 h-1.5 bg-neural-primary rounded-full animate-bounce [animation-delay:0.2s]"></span>
              <span class="w-1.5 h-1.5 bg-neural-primary rounded-full animate-bounce [animation-delay:0.4s]"></span>
            </div>
          </div>

        </div>

        <!-- RESPUESTAS RÁPIDAS (SUGERENCIAS DE CONVERSIÓN) -->
        <div v-if="messages.length === 1" class="px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar bg-neural-bg border-t border-neural-border pt-3">
          <button @click="sendQuickReply('sales')" class="whitespace-nowrap px-3.5 py-1.5 rounded-full bg-neural-card border border-neural-border text-[11px] font-bold text-neural-muted hover:bg-neural-primary hover:text-white hover:border-neural-primary transition-all shadow-sm active:scale-95 cursor-pointer">
            {{ chat.suggestions.sales }}
          </button>
          <button @click="sendQuickReply('leads')" class="whitespace-nowrap px-3.5 py-1.5 rounded-full bg-neural-card border border-neural-border text-[11px] font-bold text-neural-muted hover:bg-neural-primary hover:text-white hover:border-neural-primary transition-all shadow-sm active:scale-95 cursor-pointer">
            {{ chat.suggestions.leads }}
          </button>
          <button @click="sendQuickReply('multi')" class="whitespace-nowrap px-3.5 py-1.5 rounded-full bg-neural-card border border-neural-border text-[11px] font-bold text-neural-muted hover:bg-neural-primary hover:text-white hover:border-neural-primary transition-all shadow-sm active:scale-95 cursor-pointer">
            {{ chat.suggestions.multi }}
          </button>
        </div>

        <!-- ÁREA DE INPUT -->
        <div class="p-3 bg-neural-card border-t border-neural-border">
          <div class="relative flex items-center gap-2 bg-[#131316] p-1.5 rounded-2xl border border-neural-border transition-all focus-within:border-neural-primary focus-within:ring-2 focus-within:ring-neural-primary/20">
            <input v-model="userInput"
                   type="text"
                   @keyup.enter="sendMessage"
                   :disabled="isTyping"
                   :placeholder="chat.placeholder || 'Escribe tu mensaje...'"
                   class="flex-1 bg-transparent border-none focus:ring-0 text-sm px-3 py-2 text-neural-text placeholder:text-neural-muted outline-none disabled:opacity-50">

            <button @click="sendMessage"
                    :disabled="!userInput.trim() || isTyping"
                    class="p-2.5 bg-neural-card border border-neural-border rounded-xl text-neural-text hover:text-white hover:border-neural-primary hover:bg-neural-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm cursor-pointer">
              <svg class="w-4 h-4 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
            </button>
          </div>
        </div>

      </div>
    </Transition>

    <!-- BOTÓN FLOTANTE PRINCIPAL (NEURALS GLOW) -->
    <button @click="toggleChat" class="group relative w-14 h-14 bg-neural-primary text-white rounded-full shadow-glow-primary flex items-center justify-center hover:bg-neural-primary-hover hover:scale-105 transition-all duration-300 z-50 cursor-pointer">
      <svg v-if="!isOpen" class="w-6 h-6 transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
      <svg v-else class="w-6 h-6 transition-transform duration-300 group-hover:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M6 18L18 6M6 6l12 12"></path></svg>

      <!-- Notificación LED Verde Online -->
      <span v-if="!isOpen" class="absolute top-0 right-0 w-4 h-4 bg-neural-online rounded-full border-2 border-neural-bg flex items-center justify-center shadow-md">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-neural-online opacity-75"></span>
      </span>
    </button>

  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue';
import api from '@/api'; // Mantén tu ruta de API configurada

const isOpen = ref(false);
const userInput = ref('');
const isTyping = ref(false);
const messagesContainer = ref(null);

// ID del personaje / asistente configurado en tu backend (Ajusta al de Neurals)
const CHARACTER_ID = 29;
const emit = defineEmits(['navigate']);

// Configuración y textos adaptados a Neurals.pro (SaaS / Agentes Cognitivos)
const chat = {
  agent_name: 'Agente Neurals',
  role: 'Asesor Especialista',
  status_online: 'Sistema en línea',
  status_typing: 'Procesando consulta...',
  placeholder: 'Hazme una pregunta técnica...',
  welcome: '¡Hola! ⚡ Soy un Agente Inteligente operando con la tecnología de Neurals.pro. ¿Quieres ver cómo aplico la Arquitectura RAG o cómo extraigo leads?',
  suggestions: {
    sales: '🤖 Automatizar Ventas',
    leads: '🎯 Capturar Leads',
    multi: '📱 Gestión Multilínea'
  }
};

const messages = ref([
  { text: chat.welcome, isUser: false }
]);

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const toggleChat = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) scrollToBottom();
};

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const sendMessage = async () => {
  if (!userInput.value.trim() || isTyping.value) return;
  const storedToken = localStorage.getItem('trd_guest_token');
  const text = userInput.value;

  messages.value.push({ text: text, isUser: true });
  userInput.value = '';
  scrollToBottom();

  isTyping.value = true;
  scrollToBottom();

  try {
    const storageUser = localStorage.getItem('user');
    const userData = storageUser ? JSON.parse(storageUser) : { id: null };

    const response = await api.post('/chat', {
      guest_token: storedToken,
      character_id: CHARACTER_ID,
      message: text
    });

    const aiResponse = response.data?.ai_response;
    if (response.data?.guest_token) {
      localStorage.setItem('trd_guest_token', response.data.guest_token);
    }

    const writingTime = Math.min(aiResponse.length * 45, 1800);
    await wait(writingTime);

    if (aiResponse) {
      messages.value.push({ text: aiResponse, isUser: false });
    } else {
      messages.value.push({ text: "Lo siento, no pude procesar la consulta de datos en este momento.", isUser: false });
    }

  } catch (error) {
    console.error("Error en chat Neurals:", error);
    messages.value.push({
      text: "Tiempo de espera de conexión agotado. Por favor, intenta de nuevo o agenda una demostración desde los botones principales.",
      isUser: false
    });
  } finally {
    isTyping.value = false;
    scrollToBottom();
  }
};

const sendQuickReply = (type) => {
  let text = '';
  if (type === 'sales') text = 'Me interesa automatizar mis ventas usando Arquitectura RAG para leer mi catálogo.';
  if (type === 'leads') text = 'Quiero automatizar la captura de prospectos y cualificarlos en WhatsApp.';
  if (type === 'multi') text = 'Necesito gestionar múltiples líneas de WhatsApp desde un panel centralizado.';

  userInput.value = text;
  sendMessage();
};

onMounted(async () => {
  const storedToken = localStorage.getItem('trd_guest_token');

  if (storedToken) {
    try {
      const response = await api.get('/chat', {
        params: {
          guest_token: storedToken,
          character_id: CHARACTER_ID
        }
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        messages.value = response.data;
        scrollToBottom();
      }
    } catch (error) {
      console.error("No se pudo recuperar el historial de Neurals:", error);
    }
  }
});
</script>

<style scoped>
/* Animación de entrada fluida */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* Ocultar barra de scroll nativa, pero permitir scroll */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Animación sutil de los 3 puntos de escritura / procesamiento */
@keyframes bounceCustom {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.animate-bounce { animation: bounceCustom 0.8s infinite; }
</style>